import { useEffect, useState } from "react";
import { Button, Modal, Form } from 'react-bootstrap';

import { showToast } from "../utils/Toasts";
import { uploadFile, softDeleteFile, getFileMD5 } from "../utils/Storage"
import { updateDatabaseRecord } from "../utils/Database";

import useUserStore from "../stores/User";

import { LoadingText } from "./LoadingText";

type UploadModalProps = {
    show: boolean;
    onConfirm?: () => void | Promise<void> | (() => Promise<void>);
    onHide: () => void;
    fileInfo?: any;
}

export function UploadModal({ show, onConfirm, onHide, fileInfo }: UploadModalProps) {
    const [uploading, setUploading] = useState(false);
    const [clearFlag, setClearFlag] = useState(false);
    const [fileChanged, setFileChanged] = useState(false);
    const userData = useUserStore((state) => state.data);

    const handleConfirm = async () => {
        if (onConfirm) {
            const result = onConfirm();
            if (typeof result === 'function') {
                await result();
            }
        }
    };

    const handleUpload = async ({file}) => {
        // upload the new file to storage
        const uploadResult = await uploadFile("files", file);

        if (!uploadResult) {
            console.error("Unable to upload file:", file.name);
            return;
        }

        const newFilePath = uploadResult.path;
        const hash = await getFileMD5(newFilePath);

        const updates = {
            "Title": newFilePath,
            "storage_hash": hash,
            ...(clearFlag && { "flag_reason": null })
        };

        const updateResult = await updateDatabaseRecord(fileInfo["file_id"], userData.user_id, updates, false);

        if (!updateResult) {
            showToast("error", "Failed to update database record.");
            return;
        } else {
            if (newFilePath !== fileInfo["Title"]) {
                const deleteResult = await softDeleteFile("files", fileInfo["Title"]);
                if (!deleteResult) {
                    showToast("error", "Unable to delete previous file.");
                }
            }
        }
        setFileChanged(true);
        handleConfirm();
    };

    useEffect(() => {
        if(fileInfo && fileInfo["flag_reason"]) {
            setClearFlag(true);
        }
    }, [fileInfo]);

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
        >
            <Modal.Header>
                <Modal.Title>Upload New File</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Label>Upload a new file to replace the current one.</Form.Label>
                {uploading ? (
                    <p className="mt-3">
                        <LoadingText text="Uploading..." className="mb-0" />
                    </p>
                ) : (
                    <>
                        <Form.Control
                            type="file"
                            accept=".pdf,.doc,.docx,.txt"
                            onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                                const files = e.target.files;
                                if (files && files.length > 0) {
                                    const file = files[0];
                                    setUploading(true);
                                    handleUpload({file}).then(() => {
                                        setUploading(false);
                                    }).catch((error) => {
                                        showToast("error", "Error uploading file: " + error.message);
                                        setUploading(false);
                                    });
                                }
                            }}
                        />
                        {fileInfo && fileInfo["flag_reason"] && (
                            <Form.Check 
                                type="checkbox"
                                className="text-danger mt-3"
                                label={`Clear "${fileInfo["flag_reason"]}" flag`}
                                checked={clearFlag}
                                onChange={() => setClearFlag(!clearFlag)}
                            />
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="secondary"
                    onClick={onHide}
                    disabled={uploading || fileChanged}
                >
                    Cancel
                </Button>
            </Modal.Footer>
        </Modal>
    );
}